import React, { useEffect } from 'react'
import {
  categoryListQueryOption,
  hotDealCouponListQueryOption,
  mainTopBannerListQueryOption,
  mdPickCouponListQueryOption,
  mostViewedCouponListQueryOption,
  recommendBrandListQueryOption,
  searchPopularCouponQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  CategoryListSpace,
  HotDealItemListsSpace,
  BannerSpace,
  MdPickCouponSpace,
  MostViewedCouponListSpace,
  PopularCouponListSpace,
  RecommendBrandListSpace,
} from 'src/types/api'
import { Loading } from 'src/components/common/Loading'
import MainPageView from 'src/pages/MainPage/view'
import RenderIf from 'src/components/common/RenderIf'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { useModal } from 'src/provider/ModalContextProvider'
import RecommendAppInstallation from 'src/components/common/Modal/children/RecommendAppInstallation'
import { useCookies } from 'react-cookie'
import { mobileOS } from 'src/utils'
import usePartnerEntry from 'src/hooks/usePartnerEntry.hook'

export interface MainPageProps {
  hotDealCouponList?: HotDealItemListsSpace.HotDealItemType[]
  popularCouponList?: PopularCouponListSpace.PopularCouponItemType[]
  categoryList?: CategoryListSpace.CategoryInfoType[]
  mainTopBannerList?: BannerSpace.BannerType[]
  mdPickCouponList: MdPickCouponSpace.MdChoice[]
  recommendBrandList: RecommendBrandListSpace.MdBrandChoice[]
  mostViewedCouponList: MostViewedCouponListSpace.MostViewedCouponType[]
}

//skeleton을 위한 default 데이터
const skeletonDefaultData = {
  Hotdeal: Array(3).fill({}),
  Banner: Array(3).fill({}),
  MDChoice: Array(2).fill({
    Title: '',
    Contents: '',
    Coupons: Array(8).fill({}),
  }),
  MDBrandChoice: Array(1).fill({
    Title: '',
    Contents: '',
    Brands: Array(4).fill({}),
  }),
  MostViewedCoupons: Array(5).fill({}),
  PopularCoupon: Array(5).fill({}),
  CategoryInfo: Array(8).fill({}),
}

const MainPage = () => {
  const { web } = useChannel()
  const { openModal } = useModal()
  const [cookies] = useCookies(['app_install_popup'])
  const os = mobileOS()
  const {
    redirectFunction: { handleEnterRedirect },
  } = usePartnerEntry()

  useEffect(() => {
    // 바로 다른 페이지로 리다이렉트된다면 설치유도 팝업 노출 생략
    handleEnterRedirect().then((isPopupOpen) => {
      if (isPopupOpen) {
        if (web && os !== 'other' && !cookies['app_install_popup'])
          openModal({ contents: { children: <RecommendAppInstallation /> } })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 뒤로가기 방지
  useEffect(() => {
    const preventGoBack = () => {
      window.history.pushState(null, '', window.location.href)
    }

    window.history.pushState(null, '', window.location.href)
    window.addEventListener('popstate', preventGoBack)

    return () => {
      window.removeEventListener('popstate', preventGoBack)
    }
  }, [])

  const {
    data: hotDealItemsQueryData = { Body: { HotdealCoupon: skeletonDefaultData.Hotdeal } },
    isLoading: isHotDealItemsLoading,
  } = useCustomQuery<HotDealItemListsSpace.Response>(hotDealCouponListQueryOption())

  // 지금 인기 있는 상품
  const {
    data: popularCouponQueryData = { Body: { PopularCoupon: skeletonDefaultData.PopularCoupon } },
  } = useCustomQuery<PopularCouponListSpace.Response>(searchPopularCouponQueryOption())
  // 사람들이 많이 본 상품
  const {
    data: mostViewedCouponQueryData = {
      Body: { MostViewedCoupons: skeletonDefaultData.MostViewedCoupons },
    },
  } = useCustomQuery<MostViewedCouponListSpace.Response>(mostViewedCouponListQueryOption())
  //상단 배너 데이터
  const { data: mainTopBannerQueryData = { Body: { banner: skeletonDefaultData.Banner } } } =
    useCustomQuery<BannerSpace.Response>(mainTopBannerListQueryOption())
  //md 추천 쿠폰 데이터
  const { data: mdPickCouponQueryData = { Body: { MDChoice: skeletonDefaultData.MDChoice } } } =
    useCustomQuery<MdPickCouponSpace.Response>(mdPickCouponListQueryOption())
  //카테고리 데이터
  const { data: categoryQueryData = { Body: { CategoryInfo: skeletonDefaultData.CategoryInfo } } } =
    useCustomQuery<CategoryListSpace.Response>(categoryListQueryOption())
  //추천 브랜드 데이터
  const {
    data: recommendBrandQueryData = { Body: { MDBrandChoice: skeletonDefaultData.MDBrandChoice } },
  } = useCustomQuery<RecommendBrandListSpace.Response>(recommendBrandListQueryOption())

  const props: MainPageProps = {
    hotDealCouponList: hotDealItemsQueryData?.Body?.HotdealCoupon,
    popularCouponList: popularCouponQueryData?.Body?.PopularCoupon,
    categoryList: categoryQueryData?.Body?.CategoryInfo,
    mainTopBannerList: mainTopBannerQueryData?.Body?.banner,
    mdPickCouponList: mdPickCouponQueryData.Body.MDChoice,
    recommendBrandList: recommendBrandQueryData.Body.MDBrandChoice,
    mostViewedCouponList: mostViewedCouponQueryData.Body.MostViewedCoupons,
  }

  return (
    <React.Fragment>
      <RenderIf when={isHotDealItemsLoading}>
        <Loading.Default />
      </RenderIf>
      <MainPageView {...props} />
    </React.Fragment>
  )
}
export default MainPage
